import { useCallback } from 'react'

import { User } from '@/shared/types/user'

import { useAnalytics } from './useAnalytics'

export function useUsersAnalytics() {
  const { track, shouldSendOnce } = useAnalytics()

  const trackUserProfileViewed = useCallback(
    (user: User) => {
      if (shouldSendOnce('users_profile_viewed', user.id)) {
        track.event('users_profile_viewed', {
          viewed_user_id: user.id,
          viewed_user_name: user.name,
          viewed_user_uid: user.uid
        })
      }
    },
    [track, shouldSendOnce]
  )

  const trackUserFollowed = useCallback(
    (user: User) => {
      track.event('users_followed', {
        followed_user_id: user.id,
        followed_user_name: user.name,
        followed_user_uid: user.uid
      })
    },
    [track]
  )

  const trackUserUnfollowed = useCallback(
    (user: User) => {
      track.event('users_unfollowed', {
        unfollowed_user_id: user.id,
        unfollowed_user_name: user.name,
        unfollowed_user_uid: user.uid
      })
    },
    [track]
  )

  const trackUsersListFiltered = useCallback(
    (filterType: 'all' | 'following' | 'followers', resultsCount: number) => {
      if (
        shouldSendOnce(
          `users_filtered_${filterType}`,
          `users_${filterType}`,
          1000 * 30
        )
      ) {
        track.event('users_list_filtered', {
          filter_type: filterType,
          results_count: resultsCount
        })
      }
    },
    [track, shouldSendOnce]
  )

  const trackUserMessageSent = useCallback(
    (recipientUser: User) => {
      track.event('users_message_sent', {
        recipient_user_id: recipientUser.id,
        recipient_user_name: recipientUser.name,
        recipient_user_uid: recipientUser.uid
      })
    },
    [track]
  )

  const trackUserFeedInteraction = useCallback(
    // `uid` no lugar do id interno: os payloads de terceiros não expõem mais
    // o id numérico (minimização de dados — LGPD/ECA).
    (interactionType: 'clicked' | 'shared' | 'liked', userUid: string) => {
      track.event('users_feed_interaction', {
        interaction_type: interactionType,
        target_user_uid: userUid
      })
    },
    [track]
  )

  return {
    trackUserProfileViewed,
    trackUserFollowed,
    trackUserUnfollowed,
    trackUsersListFiltered,
    trackUserMessageSent,
    trackUserFeedInteraction
  }
}
